
import {
  defineComponent, onMounted,
  PropType,
  ref,
  SetupContext,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { StringMap } from '@/types';
import Tooltip from 'primevue/tooltip';

export default defineComponent({
  name: 'ButtonGroup',

  directives: {
    tooltip: Tooltip,
  },

  props: {
    modelValue: {
      type: [Array, String, Number] as PropType<string[] | string | number>,
      required: true,
    },

    options: {
      type: Array as PropType<any[]>,
      required: true,
      default() {
        return [];
      },
    },

    title: {
      type: String,
      required: false,
      default: '',
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    tooltipDesc: {
      type: String,
      required: false,
      default: '',
    },

    tooltipClass: {
      type: String,
      required: false,
      default: '',
    },

    customClass: {
      type: String,
      required: false,
      default: '',
    },

    customClassTitle: {
      type: String,
      required: false,
      default: '',
    },

    mustTranslateLabel: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowEmpty: {
      type: Boolean,
      default: true,
    },

    mode: {
      type: String as PropType<'button' | 'card'>,
      default: 'button',
    },

    layout: {
      type: String as PropType<'horizontal' | 'vertical'>,
      default: 'horizontal',
    },
  },

  emits: ['update:modelValue'],

  setup(props: {
    modelValue: number | string | string[];
    options: any[];
    mustTranslateLabel: boolean;
    multiple: boolean;
    allowEmpty: boolean;
    mode: 'button' | 'card';
    layout: 'horizontal' | 'vertical';
  }, { emit }: SetupContext) {
    const { t } = useI18n();

    const data = ref(props.modelValue);
    const optionsValues = ref(props.options);

    watch([data], () => emit('update:modelValue', data.value));

    watch(() => props.modelValue, (newVal) => {
      data.value = newVal;
    });

    onMounted(() => {
      if (props.mustTranslateLabel) {
        optionsValues.value = optionsValues.value.map((item: any) => ({
          ...item,
          label: t(item.label),
        }));
      }
    });

    const isSelected = (value: string | number) => {
      if (props.multiple && Array.isArray(data.value)) {
        return data.value.includes(value as string);
      }
      return data.value === value;
    };

    const toggleOption = (value: string | number) => {
      if (props.multiple && Array.isArray(data.value)) {
        const index = data.value.indexOf(value as string);
        if (index === -1) {
          data.value = [...data.value, value as string];
        } else if (props.allowEmpty || data.value.length > 1) {
          data.value = data.value.filter((v) => v !== value);
        }
      } else {
        if (data.value === value && props.allowEmpty) {
          data.value = '';
        } else {
          data.value = value;
        }
      }
    };

    return {
      t,
      optionsValues,
      data,
      isSelected,
      toggleOption,
    };
  },
});
